import React, { useContext, useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Title,
  Button,
  Group,
  Box,
  Text,
  Paper,
  Alert,
  Select,
} from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { MyUserDataContext } from "../../contexts";
import { CustomClaims } from "../../types";
import { GenevaFormGenerator } from "./components/GenevaFormGenerator";
import { SchemaType, schemaTypeOptions } from "../../types/schema";
import { DEFAULT_SCHEMA_TYPE } from "../../utils/schema-config";
import { getSchemaDefaults } from "../../utils/geneva-schema-loader";

export default function GenevaRequestCreate() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userContext = useContext(MyUserDataContext);

  const customClaims = (userContext.firebaseTokenResult?.claims?.customClaims as CustomClaims) || {};
  const canCreate = Boolean(customClaims.geneva_requests_create);

  // Get orgId and templateId from URL params or defaults
  const orgId = searchParams.get('orgId') || 'demo';
  const rawSchemaType = searchParams.get('schemaType');
  const initialSchemaType: SchemaType = rawSchemaType === 'gwi' ? 'gwi' : DEFAULT_SCHEMA_TYPE;
  const templateParam = searchParams.get('templateId');
  const defaultTemplateId = initialSchemaType === 'gwi' ? 'gwi' : 'default';
  const templateId = templateParam || defaultTemplateId;
  const [schemaType, setSchemaType] = useState<SchemaType>(initialSchemaType);
  const schemaDefaults = useMemo(() => getSchemaDefaults(schemaType), [schemaType]);
  const schemaLabel = schemaDefaults.displayName;

  useEffect(() => {
    const nextType: SchemaType = rawSchemaType === 'gwi' ? 'gwi' : DEFAULT_SCHEMA_TYPE;
    if (nextType !== schemaType) {
      setSchemaType(nextType);
    }
  }, [rawSchemaType, schemaType]);

  const handleSchemaTypeChange = (value: string | null) => {
    if (!value) return;
    const nextType: SchemaType = value === 'gwi' ? 'gwi' : DEFAULT_SCHEMA_TYPE;
    if (nextType === schemaType) return;
    setSchemaType(nextType);

    const params = new URLSearchParams(searchParams);
    const nextTemplateId = nextType === 'gwi' ? 'gwi' : 'default';
    params.set('schemaType', nextType);
    params.set('orgId', orgId);
    params.set('templateId', nextTemplateId);
    setSearchParams(params);
  };

  if (!canCreate) {
    return (
      <Container size="xl" py="md">
        <Group justify="space-between" mb="xl">
          <Box>
            <Title order={2}>Create New {schemaLabel} Request</Title>
            <Text c="dimmed" size="sm">
              You do not have permission to create or edit {schemaLabel} requests.
            </Text>
          </Box>
          <Button
            variant="subtle"
            size="compact-md"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/geneva_requests')}
          >
            Back to Requests
          </Button>
        </Group>
        <Alert color="red" title="Not authorized">
          Contact an administrator if you believe you should have access to create {schemaLabel} requests.
        </Alert>
      </Container>
    );
  }

  const handleSave = (requestId: string) => {
    const params = new URLSearchParams();
    params.set('schemaType', schemaType);
    params.set('orgId', orgId);
    params.set('templateId', templateId);
    navigate(`/geneva_requests/${requestId}?${params.toString()}`);
  };

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="xl">
        <Box>
          <Group gap="sm" align="center">
            <Button
              variant="subtle"
              size="compact-md"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/geneva_requests')}
            >
              Back to Requests
            </Button>
          </Group>
          <Title order={2} mt="sm">Create New {schemaLabel} Request</Title>
          <Text c="dimmed" size="sm">
            Fill out the form below to create a new {schemaLabel} request
          </Text>
        </Box>
        <Select
          size="sm"
          data={schemaTypeOptions}
          value={schemaType}
          onChange={handleSchemaTypeChange}
          comboboxProps={{ withinPortal: true }}
          aria-label="Schema type"
        />
      </Group>

      <Paper shadow="sm" p="md" withBorder>
        <GenevaFormGenerator
          orgId={orgId}
          templateId={templateId}
          onSave={handleSave}
          schemaType={schemaType}
        />
      </Paper>
    </Container>
  );
}
